// 外观：深浅（跟随系统 / 浅色 / 深色）、配色（暖白 · 墨 / 纸面，只在浅色下生效）、标题字体（宋体 / 黑体）。
// 写到 <html> 的 data-lingzhi-* 上，CSS 变量（lingzhi-design 生成）跟着变；antd 用同一份令牌生成的主题对象。
// 默认值与 lingzhi-design 一致：跟随系统 + 暖白 · 墨 + 宋体，默认值不写属性。
// 2026-10-11 默认配色从「纸面」换成「暖白 · 墨」：以前存下的设置（没有 v）里的配色一律换成新默认，深浅和字体照旧。

import { useCallback, useEffect, useState } from 'react'
import type { LingzhiAppearance } from '@lingzhi/design-tokens/antd'

export type ThemeChoice = 'system' | 'light' | 'dark'
export type Tone = 'ink' | 'paper'
export type TitleFont = 'serif' | 'sans'
export interface Appearance {
  theme: ThemeChoice
  tone: Tone
  title: TitleFont
}

export const APPEARANCE_KEY = 'lingzhi-web.appearance'
export const DEFAULT_APPEARANCE: Appearance = { theme: 'system', tone: 'ink', title: 'serif' }
const VERSION = 2

const pick = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T => (allowed.includes(v as T) ? (v as T) : fallback)

export function loadAppearance(): Appearance {
  try {
    const raw = JSON.parse(localStorage.getItem(APPEARANCE_KEY) ?? '{}') as Partial<Appearance> & { v?: number }
    return {
      theme: pick(raw.theme, ['system', 'light', 'dark'] as const, DEFAULT_APPEARANCE.theme),
      tone: raw.v === VERSION ? pick(raw.tone, ['ink', 'paper'] as const, DEFAULT_APPEARANCE.tone) : DEFAULT_APPEARANCE.tone,
      title: pick(raw.title, ['serif', 'sans'] as const, DEFAULT_APPEARANCE.title),
    }
  } catch {
    return DEFAULT_APPEARANCE
  }
}

export function saveAppearance(a: Appearance) {
  try {
    localStorage.setItem(APPEARANCE_KEY, JSON.stringify({ ...a, v: VERSION }))
  } catch {
    // 隐私模式等拿不到存储时，只在这一页生效
  }
}

export function applyAppearance(a: Appearance, root: HTMLElement = document.documentElement) {
  const set = (key: 'lingzhiTheme' | 'lingzhiTone' | 'lingzhiTitle', value: string | null) => {
    if (value === null) delete root.dataset[key]
    else root.dataset[key] = value
  }
  set('lingzhiTheme', a.theme === 'system' ? null : a.theme)
  set('lingzhiTone', a.tone === 'ink' ? null : a.tone)
  set('lingzhiTitle', a.title === 'serif' ? null : a.title)
}

/** 当前实际生效的外观名（antd 主题对象按它取）。 */
export function effectiveAppearance(a: Appearance, systemDark: boolean): LingzhiAppearance {
  if (a.theme === 'dark' || (a.theme === 'system' && systemDark)) return 'dark'
  return a.tone === 'paper' ? 'light-paper' : 'light-ink'
}

const darkQuery = () => (typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null)

export function useAppearance() {
  const [appearance, setState] = useState<Appearance>(loadAppearance)
  const [systemDark, setSystemDark] = useState(() => darkQuery()?.matches ?? false)

  useEffect(() => {
    const q = darkQuery()
    if (!q) return
    const onChange = () => setSystemDark(q.matches)
    q.addEventListener('change', onChange)
    return () => q.removeEventListener('change', onChange)
  }, [])

  useEffect(() => applyAppearance(appearance), [appearance])

  const setAppearance = useCallback((patch: Partial<Appearance>) => {
    setState((prev) => {
      const next = { ...prev, ...patch }
      saveAppearance(next)
      return next
    })
  }, [])

  const effective = effectiveAppearance(appearance, systemDark)
  return { appearance, setAppearance, effective, isDark: effective === 'dark' }
}
